"use client";

import React from "react";

import DecorationLine from "@/components/global/ui/DecorationLine";
import CornerChip from "@/components/global/ui/CornerChip";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import Chip from "@/components/global/ui/Chip";
import {AnimatedAsset6} from "@/components/global/animatedAssets/Asset6";
import {AnimatedAsset7} from "@/components/global/animatedAssets/Asset7";
import {AnimatedAsset8} from "@/components/global/animatedAssets/Asset8";
import {AnimatedAsset9} from "@/components/global/animatedAssets/Asset9";

import {PageLeaderboardsSchema} from "@/sanity/pages/PageLeaderboardsTemplate";

const animationMap = [AnimatedAsset6, AnimatedAsset7, AnimatedAsset8, AnimatedAsset9];

export default function HowSection({
  data,
  className,
}: {
  data: PageLeaderboardsSchema;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative overflow-clip pt-[10rem] lg:pt-[14.5rem] pb-[8rem] lg:pb-[16rem]",
        className
      )}
    >
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />
      <DecorationLine
        className="left-0 top-0"
        orientation="horizontal"
      />

      <DecorationSquares
        className="top-[2px]"
      />

      <div className="container md:max-w-[112rem] lg:max-w-[216.5rem] flex flex-col items-center">
        <Chip
          image={data?.howChip?.icon}
          text={data?.howChip?.text}
        />

        {data?.howText && (
          <div className="richtext w-[min(65rem,100%)] mb-[3rem] lg:mb-[6rem]">
            <PortableText
              value={data.howText}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.howItems && (
          <div className="flex flex-col items-center md:items-stretch md:grid md:grid-cols-2 lg:grid-cols-4 gap-[2rem]">
            {data?.howItems.map((item, index) => {
              const AnimatedAsset = animationMap[index] || null;

              return (
                <div
                  key={index}
                  className="bg-white w-[min(57.5rem,100%)] md:w-full"
                >
                  {AnimatedAsset && (
                    <AnimatedAsset language={data?.settings?.language ?? "de"} />
                  )}

                  <div className="relative p-[3rem] sm:p-[4rem]">
                    <CornerChip
                      className="absolute -left-[1px] -translate-y-px bottom-full"
                      image={item?.chip?.icon}
                      text={item?.chip?.text}
                    />

                    {item?.text && (
                      <div className="richtext richtext-start prose-p:text-black/80">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
